import React, { useMemo, useRef, type JSX } from 'react'
import { Button, SkipNavContent, SkipNavLink } from '@chakra-ui/react'
import Head from 'next/head'
import classNames from 'classnames'
import { StickyBanner } from '@reward-platform/lift/components'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { Section } from '@alto-avios/alto-ui'
import Footer from '~/components/shared/Footer/Footer'
import { Header } from '~/components/shared/Header'
import SpinnerOverlay from '~/components/shared/SpinnerOverlay'
import { usePlatformTheme } from '~/context/theme'
import { usePlatformConfig } from '~/context/platform'
import { useUser } from '~/context/user'
import { useAuthContext } from '~/context/auth'
import { getAuthUserDisplayName } from '~/context/auth/Auth.types'
import { TagManagerProvider } from '~/context/analytics'
import { useAccount } from '~/context/account'
import { useBasketItemsCount } from '~/hooks/useBasket/useBasket'
import { getSharedSpacesEnvironment } from '~/utils/getSharedSpacesEnvironment'
import { isChatbotEnabledForOpCo } from '~/utils/chatbot'
import { getOpCoMetaConfig } from '@reward-platform/platform-config'
import { useEnsureHotelBoosterOnlyOnOrdersPage } from '~/components/orders/hooks/useEnsureHotelBoosterOnlyOnOrdersPage'
import { LayoutProps } from './Layout.types'
import BARCMemberLayout from './BARCMemberLayout'
import { StickyBanners } from '../StickyBanners'
import NotificationBar from '../Notification/NotificationBar'
import { useSignInBenefitsModal } from '../SignInBenefitsModal/useSignInBenefitsModal'
import { useGlobalStickyOffsets } from './useGlobalStickyOffsets'

import styles from './Layout.module.scss'

const SKIP_TO_CONTENT_ID = 'skip-to-content'
const SKIP_TO_FOOTER_ID = 'skip-to-footer'
const sharedSpacesAccessToken = process.env.NEXT_PUBLIC_SHARED_SPACES_CMS_ACCESS_TOKEN ?? ''

const ChatWidgetMock = dynamic(() => import('../ChatWidgetMock'), { ssr: false })
// Only rendered while open, so its code is loaded only for users who see it
const SignInBenefitsModal = dynamic(
  () =>
    import('../SignInBenefitsModal/SignInBenefitsModal').then(
      (module) => module.SignInBenefitsModal
    ),
  { ssr: false }
)
const SharedComponentsProvider = dynamic(
  () => import('@channel/shared-header-footer').then((module) => module.SharedComponentsProvider),
  { ssr: false }
)

type BaseLayoutProps = LayoutProps & {
  isLoading?: boolean
}

const BaseLayout = ({
  children,
  title,
  sparse = false,
  variant = 'base',
  isLoading,
}: BaseLayoutProps): JSX.Element => {
  const { opCo } = usePlatformConfig()
  const opCoMeta = getOpCoMetaConfig(opCo)
  const { isAviosTheme } = usePlatformTheme()
  const notificationLayerRef = useRef<HTMLDivElement>(null)
  const stickyBannersLayerRef = useRef<HTMLDivElement>(null)
  const stickyOffsetStyle = useGlobalStickyOffsets(notificationLayerRef, stickyBannersLayerRef)

  const { aviosBalance } = useAccount()
  const { isAuthenticated, user, userId } = useAuthContext()
  const basketItemsCount = useBasketItemsCount()
  const signInBenefitsModal = useSignInBenefitsModal()

  const userDisplayName = useMemo(() => getAuthUserDisplayName(user), [user])
  const membershipNumber = useMemo(() => userId ?? '', [userId])

  const t = useTranslations('components.layout.actions')

  const pageTitle = title ? `${opCoMeta.name} - ${title}` : `Avios - ${opCoMeta.name}`

  const ContentWrapper = useMemo(() => {
    switch (variant) {
      case 'no-section-wrapper':
        return ({ children: wrappedChildren }: React.PropsWithChildren) => wrappedChildren
      case 'base':
      default:
        return ({ children: wrappedChildren }: React.PropsWithChildren) => (
          <Section
            alignItems="flexStart"
            justifyContent="flexStart"
            flexDirection="column"
            paddingY={0}
          >
            <Section.Container>{wrappedChildren}</Section.Container>
          </Section>
        )
    }
  }, [variant])

  return (
    <TagManagerProvider>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Turn your everyday spending into exciting travel rewards - discover more &amp; shop with Avios."
        />
      </Head>
      <StickyBanner.StickyContainer
        ref={notificationLayerRef}
        className={styles.stickyNotificationLayer}
        data-testid="sticky-notification-layer"
      >
        <NotificationBar />
      </StickyBanner.StickyContainer>
      {/* inert hides the page from assistive technology and input while the modal is open */}
      <div
        className={classNames(styles.wrapper, { [variant]: true })}
        style={stickyOffsetStyle}
        inert={signInBenefitsModal.isOpen}
      >
        {!isAviosTheme ? (
          <>
            {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <SkipNavLink id={SKIP_TO_CONTENT_ID} className={styles.skipLink}>
                <Button variant={'link' as any} tabIndex={-1}>
                  {t('skip_to_main_content')}
                </Button>
              </SkipNavLink>
            }
            {!sparse ? (
              <SkipNavLink id={SKIP_TO_FOOTER_ID} className={styles.skipLink}>
                <Button variant={'link' as any} tabIndex={-1}>
                  {t('skip_to_footer')}
                </Button>
              </SkipNavLink>
            ) : null}
          </>
        ) : null}
        {sparse || (
          <Header
            userDisplayName={userDisplayName}
            membershipNumber={membershipNumber}
            aviosBalance={aviosBalance ?? 0}
            basketItemsCount={basketItemsCount}
            isLoading={isLoading}
            skipContentSelector={SKIP_TO_CONTENT_ID}
          />
        )}
        <StickyBanners
          ref={stickyBannersLayerRef}
          className={styles.stickyBannersLayer}
          data-testid="sticky-banners-layer"
        />
        <main id={SKIP_TO_CONTENT_ID} className={styles.content}>
          <ContentWrapper>
            {isLoading || (user === undefined && isAuthenticated) ? <SpinnerOverlay /> : children}
          </ContentWrapper>
        </main>
        {!isAuthenticated && isChatbotEnabledForOpCo(opCo) ? <ChatWidgetMock /> : null}
        {sparse || (
          <>
            <SkipNavContent id={SKIP_TO_FOOTER_ID} />
            <Footer />
          </>
        )}
      </div>
      {signInBenefitsModal.isOpen ? <SignInBenefitsModal {...signInBenefitsModal} /> : null}
    </TagManagerProvider>
  )
}

const Layout = (props: LayoutProps) => {
  const { user } = useUser()
  const environment = useMemo(() => getSharedSpacesEnvironment(), [])
  const { isAviosTheme } = usePlatformTheme()
  const { opCo } = usePlatformConfig()
  useEnsureHotelBoosterOnlyOnOrdersPage()

  const isBARCmember = opCo === 'BA' && user?.memberIdentifier?.identifier?.startsWith('OWVE')

  if (isBARCmember) {
    return <BARCMemberLayout />
  }

  if (isAviosTheme) {
    return (
      <SharedComponentsProvider
        cms={{
          accessToken: sharedSpacesAccessToken,
          environment,
        }}
      >
        <BaseLayout {...props} isLoading={false} />
      </SharedComponentsProvider>
    )
  }

  return <BaseLayout {...props} isLoading={false} />
}

export const AuthenticatedEmptyPageLayout = ({ children }: LayoutProps) => {
  const { user, isLoading: isUserLoading } = useUser()
  const userIsLoaded = user && !isUserLoading

  return userIsLoaded ? (
    <TagManagerProvider>
      <Section
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        paddingX="lg"
        paddingY="lg"
      >
        {children}
      </Section>
    </TagManagerProvider>
  ) : null
}

export default Layout
